import { BASE_INV_SLOTS } from './constants.js';
import type { ItemId } from './content/items.js';
import { levelOf, xpFor, type SkillId } from './content/skills.js';
import { STARTER_TOOLS, TOOLS, isToolId, type ToolId } from './content/tools.js';
import type { WaterKind } from './content/fishing.js';
import type { GearId, Stance, WornSlot } from './content/equipment.js';
import type { BuffId } from './content/items.js';
import type { ObstacleKind } from './content/obstacles.js';
import type { ShopEntry } from './content/crafting.js';
import type { FactionId } from './content/factions.js';
import type { BoardState } from './content/board.js';
import type { FarmContract } from './content/farming.js';
import type { PlotState } from './content/farming.js';

/**
 * Everything persistent about a character. Characters belong to an account, not a world:
 * the same character can log into any world, one world at a time.
 *
 * Combat will add fields here (hitpoints, combat skills, equipment). Keep additions optional
 * so older saves still load; `migrateCharacter` fills defaults.
 */
export interface CharacterState {
  version: 1;
  name: string;
  xp: Record<SkillId, number>;
  /** Pack slots. A stackable item (bait, feathers) takes one slot; its count is in `stacks`. */
  inv: (ItemId | null)[];
  /** Counts for stackable items that are in the pack. */
  stacks: Partial<Record<ItemId, number>>;
  bank: Partial<Record<ItemId, number>>;
  coins: number;
  upgrades: Partial<Record<ShopEntry['id'], number>>;
  /** Obstacle object ids this character has cleared. */
  cleared: number[];
  regions: string[];
  landmarks: number[];
  /** Fog of war, one bit per tile, base64. */
  revealed: string;
  /** Fractional exploration xp waiting to be granted. */
  revealCarry: number;
  x: number;
  z: number;
  /** Ticks of "well fed" left. */
  fedTicks: number;
  /** Fishing journal: per species (and legend) caught count, best weight in kg, and where. */
  journal: Partial<Record<string, JournalEntry>>;
  /** Traps and pots this character has set. */
  traps: TrapState[];
  /** Hidden fishing spots this character has a chart for (object ids). */
  charts: number[];
  /** Current hitpoints (max is Vitality × 10). */
  hp: number;
  /** Worn weapon and armour. */
  equip: Partial<Record<WornSlot, GearId>>;
  /** "attack" trains the weapon's skill; "guard" trains Defence and defends better. */
  stance: Stance;
  /** Special attack energy, 0..100. */
  special: number;
  /** Food buffs: ticks left. */
  buffs: Partial<Record<BuffId, number>>;
  /** Healing over time from stews: [hitpoints left to give, ticks left]. */
  regen: [number, number] | null;
  /** Ore carried in an ore sack (doesn't take pack slots). */
  sack: Partial<Record<ItemId, number>>;
  /** Uses on each piece of gear or tool since it was last made or repaired, by item id. */
  wear: Partial<Record<string, number>>;
  /** Mining finds: gems, geodes, fossils and coins found, by item id. */
  finds: Partial<Record<string, number>>;
  /** Masterworks this smith has forged. */
  masterworks: Masterwork[];
  /** Bram's work order, if one is taken. */
  order: WorkOrder | null;
  /** Standing with each faction (missing: the faction's starting standing). */
  standing: Partial<Record<FactionId, number>>;
  /** Quest progress by quest id. */
  quests: Record<string, QuestProgress>;
  /** Permanent unlocks earned from quests (shortcuts, shops, story beats). */
  flags: string[];
  /** Waystones touched (object ids). */
  waystones: number[];
  /** Running (twice as fast, uses energy) or walking. */
  run: boolean;
  /** Run energy, 0..100. Running drains it; walking and standing refill it. */
  energy: number;
  /** Sneaking: slow, crouched, much harder for raiders to notice. */
  sneak: boolean;
  /** Fight back on your own when something attacks you (RuneScape's auto-retaliate). */
  autoRetaliate: boolean;
  /** Farming: this character's crop in each patch, by plotKey. */
  plots: Record<string, PlotState>;
  /** Water in the watering can. */
  water: number;
  /** A growing contract from a grower, and how many done. */
  contract?: FarmContract;
  /** Potion boosts: levels added to a skill, and ticks left. */
  boosts: Partial<Record<SkillId, [number, number]>>;
  /** The village board: today's jobs and this week's, with progress. */
  board?: BoardState;
  /** Friends: account id and the name they had when added (names are unique). */
  friends: { id: string; name: string }[];
  /** The world generation this character was last saved on (see MapFile.epoch). */
  mapEpoch?: number;
  createdAt: number;
  playedTicks: number;
}

/** Where a character is in a quest: the step they're on, counters for it, and when it was finished. */
export interface QuestProgress { step: number; n?: number; done?: number; started: number }

export interface Masterwork { item: string; name: string; at: number }
/** "Make n of item (at least this quality) and bring them to Bram." */
export interface WorkOrder { item: string; n: number; grade: 'fine' | null; coins: number; xp: number; recipe: string }

export interface JournalEntry { n: number; kg: number; where: string }
export interface TrapState { tool: ToolId; x: number; z: number; water: WaterKind; setAt: number }

/** How many friends a character can keep. */
export const MAX_FRIENDS = 100;

export function freshCharacter(name: string, spawn: [number, number], tiles: number): CharacterState {
  return {
    version: 1,
    name,
    xp: { melee: 0, ranged: 0, defence: 0, vitality: xpFor(10), forestry: 0, mining: 0, fishing: 0, cooking: 0, smithing: 0, crafting: 0, farming: 0, herblore: 0, exploration: 0 },
    inv: [...STARTER_TOOLS, ...new Array(BASE_INV_SLOTS - STARTER_TOOLS.length).fill(null)],
    stacks: {},
    bank: {},
    coins: 0,
    upgrades: {},
    cleared: [],
    regions: [],
    landmarks: [],
    revealed: encodeBits(new Uint8Array(tiles)),
    revealCarry: 0,
    x: spawn[0],
    z: spawn[1],
    fedTicks: 0,
    journal: {},
    traps: [],
    charts: [],
    hp: 100,
    equip: {},
    stance: 'attack',
    special: 100,
    buffs: {},
    regen: null,
    sack: {},
    wear: {},
    finds: {},
    masterworks: [],
    order: null,
    mapEpoch: 1,
    waystones: [],
    run: true,
    energy: 100,
    sneak: false,
    autoRetaliate: true,
    friends: [],
    plots: {},
    water: 0,
    boosts: {},
    standing: {},
    quests: {},
    flags: [],
    createdAt: Date.now(),
    playedTicks: 0,
  };
}

/**
 * Bring an older or partial save up to the current shape. `epoch` is the world's generation:
 * a character saved on an older world keeps skills, items, coins, quests and standing, but
 * everything tied to places on the old map (position, fog of war, cleared obstacles, landmarks,
 * charts, traps) starts fresh. Traps come back to the chest.
 */
export function migrateCharacter(raw: Partial<CharacterState>, spawn: [number, number], tiles: number, epoch = 1): CharacterState {
  const base = freshCharacter(raw.name ?? 'Wanderer', spawn, tiles);
  const c: CharacterState = { ...base, ...raw, version: 1 } as CharacterState;
  c.xp = { ...base.xp, ...(raw.xp ?? {}) };
  if (!Array.isArray(c.inv) || c.inv.length < BASE_INV_SLOTS) c.inv = [...(c.inv ?? []), ...new Array(BASE_INV_SLOTS).fill(null)].slice(0, Math.max(BASE_INV_SLOTS, c.inv?.length ?? 0));
  c.stacks = { ...(raw.stacks ?? {}) };
  c.bank = { ...(raw.bank ?? {}) };
  c.journal = { ...(raw.journal ?? {}) };
  c.traps = Array.isArray(raw.traps) ? raw.traps : [];
  c.charts = Array.isArray(raw.charts) ? raw.charts : [];
  c.equip = { ...(raw.equip ?? {}) };
  c.buffs = { ...(raw.buffs ?? {}) };
  c.sack = { ...(raw.sack ?? {}) };
  c.wear = { ...(raw.wear ?? {}) };
  c.finds = { ...(raw.finds ?? {}) };
  c.masterworks = Array.isArray(raw.masterworks) ? raw.masterworks : [];
  c.order = raw.order ?? null;
  c.standing = { ...(raw.standing ?? {}) };
  c.quests = { ...(raw.quests ?? {}) };
  c.flags = Array.isArray(raw.flags) ? raw.flags : [];
  c.waystones = Array.isArray(raw.waystones) ? raw.waystones : [];
  c.run = raw.run ?? true;
  c.energy = typeof raw.energy === 'number' ? Math.max(0, Math.min(100, raw.energy)) : 100;
  c.sneak = raw.sneak === true;
  c.autoRetaliate = raw.autoRetaliate !== false;
  c.plots = raw.plots && typeof raw.plots === 'object' ? { ...raw.plots } : {};
  c.water = typeof raw.water === 'number' ? Math.max(0, raw.water) : 0;
  c.boosts = raw.boosts && typeof raw.boosts === 'object' ? { ...raw.boosts } : {};
  c.contract = raw.contract && typeof raw.contract === 'object' && typeof raw.contract.done === 'number' ? { crop: raw.contract.crop ?? null, n: raw.contract.n | 0, need: raw.contract.need | 0, done: raw.contract.done | 0 } : undefined;
  c.board = raw.board && typeof raw.board === 'object' && Array.isArray(raw.board.daily) ? { ...raw.board, daily: raw.board.daily.map(t => ({ ...t })) } : undefined;
  c.friends = Array.isArray(raw.friends) ? raw.friends.filter(f => f && typeof f.id === 'string' && typeof f.name === 'string').slice(0, MAX_FRIENDS) : [];
  if (typeof raw.hp !== 'number' || raw.hp <= 0) c.hp = levelOf(c.xp.vitality) * 10;
  if ((raw.mapEpoch ?? 1) !== epoch) {
    c.x = spawn[0]; c.z = spawn[1];
    c.revealed = base.revealed; c.revealCarry = 0;
    c.cleared = []; c.landmarks = []; c.charts = []; c.waystones = [];
    c.flags = c.flags.filter(f => !f.startsWith('waystone:'));
    for (const t of c.traps) c.bank[t.tool] = (c.bank[t.tool] ?? 0) + 1;
    c.traps = [];
  }
  c.mapEpoch = epoch;
  const old = raw as Record<string, unknown>;
  if ('tools' in old || 'supplies' in old) {
    // Older saves kept tools on a belt and supplies as counters. Both move into the pack;
    // whatever doesn't fit goes to the chest.
    const toolList = migrateTools(old.tools);
    const supplies = { ...((old.supplies as Record<string, number>) ?? {}) };
    if (old.tools && !Array.isArray(old.tools)) { supplies.bait = (supplies.bait ?? 0) + 50; supplies.feathers = (supplies.feathers ?? 0) + 50; }
    const best = new Map<string, ToolId>();
    for (const id of toolList) { const d = TOOLS[id], b = best.get(d.line); if (!b || TOOLS[b].tier < d.tier) best.set(d.line, id); }
    const toPack = [...best.values()], toBank = toolList.filter(id => !toPack.includes(id));
    for (const id of toPack) { const i = c.inv.indexOf(null); if (i >= 0) c.inv[i] = id; else toBank.push(id); }
    for (const id of toBank) c.bank[id] = (c.bank[id] ?? 0) + 1;
    for (const [id, n] of Object.entries(supplies) as [ItemId, number][]) {
      if (!n) continue;
      const i = c.inv.indexOf(null);
      if (i >= 0) { c.inv[i] = id; c.stacks[id] = n; } else c.bank[id] = (c.bank[id] ?? 0) + n;
    }
    delete (c as unknown as Record<string, unknown>).tools;
    delete (c as unknown as Record<string, unknown>).supplies;
  }
  return c;
}

/**
 * Belt-era saves stored a list of tool ids; the oldest stored a tier per kind
 * ({ axe: 2, pick: 1, rod: 1 }). Either becomes a list of tools, plus the starter kit and the
 * fishing gear a player at that point would already have earned.
 */
function migrateTools(raw: unknown): ToolId[] {
  const out = new Set<ToolId>(STARTER_TOOLS);
  if (Array.isArray(raw)) { for (const id of raw) if (isToolId(id)) out.add(id); return [...out]; }
  if (raw && typeof raw === 'object') {
    const t = raw as Record<string, number>;
    const axes: ToolId[] = ['worn_hatchet', 'bronze_hatchet', 'iron_hatchet', 'star_hatchet'];
    const picks: ToolId[] = ['worn_pick', 'bronze_pick', 'iron_pick', 'star_pick'];
    for (let i = 1; i <= Math.min(3, t.axe ?? 0); i++) out.add(axes[i]);
    for (let i = 1; i <= Math.min(3, t.pick ?? 0); i++) out.add(picks[i]);
    // the old willow rod caught everything up to pike; the iron-hook rod caught glowfin
    out.add('fly_rod'); out.add('fishing_rod'); out.add('eel_trap');
    if ((t.rod ?? 0) >= 1) out.add('iron_harpoon');
    if ((t.rod ?? 0) >= 2) out.add('star_harpoon');
  }
  return [...out];
}

/** Fields of CharacterState the client is allowed to see (all of them, today). */
export type SelfState = Omit<CharacterState, 'revealCarry'>;

/* ---- bitset helpers (fog of war) ---- */
export function encodeBits(bits: Uint8Array): string {
  const bytes = new Uint8Array(Math.ceil(bits.length / 8));
  for (let i = 0; i < bits.length; i++) if (bits[i]) bytes[i >> 3] |= 1 << (i & 7);
  return bytesToB64(bytes);
}
export function decodeBits(str: string, length: number): Uint8Array {
  const bytes = b64ToBytes(str);
  const out = new Uint8Array(length);
  for (let i = 0; i < length; i++) out[i] = (bytes[i >> 3] >> (i & 7)) & 1;
  return out;
}
export function bytesToB64(bytes: Uint8Array): string {
  let s = '';
  for (let i = 0; i < bytes.length; i += 0x8000) s += String.fromCharCode(...bytes.subarray(i, i + 0x8000));
  return btoa(s);
}
export function b64ToBytes(str: string): Uint8Array {
  const s = atob(str);
  const out = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) out[i] = s.charCodeAt(i);
  return out;
}
